// ─────────────────────────────────────────────────────────────
// LE VIVIER — habitants et animaux
//
// Tout repose sur une GRILLE DE NAVIGATION construite par le moteur :
//   nav.walkable  Set des cases praticables
//                 = terre + tabliers de pont − cases occupées par un
//                   décor de la couche « objet »
//
// Conséquences directes, sans code supplémentaire :
//   · personne ne traverse un mur ou une maison (collisions)
//   · les ponts sont praticables, donc on passe d'une île à l'autre
//   · un animal posé dans un enclos y reste enfermé, puisque les murs
//     ne sont pas praticables
//
// Les déplacements passent par un BFS sur cette grille : un agent ne
// choisit jamais une destination qu'il ne peut pas atteindre, et il
// contourne les obstacles au lieu de les traverser en ligne droite.
// ─────────────────────────────────────────────────────────────
import { PAL } from "./tileset.js";

const SPEED = 0.013;
const ANIMAL_SPEED = 0.007;
const MEET_RADIUS = 8;
const TALK_RADIUS = 1.15;
const PET_RADIUS = 9;
const PET_DISTANCE = 1.3;
const TALK_MIN = 5, TALK_MAX = 10;
const PET_TIME = 4.5;
const COOLDOWN = 14;
const MAX_BFS = 4000;

const k = (x, y) => `${x},${y}`;
const dist = (a, b) => Math.hypot(a.gx - b.gx, a.gy - b.gy);

export function makeWalker(x, y, kind = "pnj") {
  return {
    kind,                       // "pnj" | "animal"
    gx: x, gy: y,
    path: null, step: 0,
    state: "pause", timer: 0.4 + Math.random() * 2.2,
    facing: 1, moving: false,
    partner: null, cooldown: Math.random() * COOLDOWN,
    bubble: null, bubbleT: 0, speaking: false,
    pettedBy: null,
  };
}

// ── Recherche de chemin ───────────────────────────────────────
// BFS 4-connexe sur les cases praticables. Borné à MAX_BFS nœuds :
// sur un archipel complet ça reste instantané, et si la destination
// est enclavée on abandonne proprement au lieu de tourner en rond.
export function findPath(nav, sx, sy, tx, ty) {
  const start = k(sx, sy), goal = k(tx, ty);
  if (start === goal) return [];
  if (!nav.walkable.has(goal)) return null;
  const prev = new Map([[start, null]]);
  const queue = [[sx, sy]];
  let head = 0, seen = 0;
  while (head < queue.length && seen++ < MAX_BFS) {
    const [cx, cy] = queue[head++];
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = cx + dx, ny = cy + dy, key = k(nx, ny);
      if (prev.has(key) || !nav.walkable.has(key)) continue;
      prev.set(key, [cx, cy]);
      if (key === goal) {
        const path = [];
        let cur = [nx, ny];
        while (cur) {
          path.push({ x: cur[0], y: cur[1] });
          cur = prev.get(k(cur[0], cur[1]));
        }
        return path.reverse().slice(1);
      }
      queue.push([nx, ny]);
    }
  }
  return null;
}

// Destination au hasard, atteignable, dans un rayon donné.
function wanderTo(agent, nav, radius) {
  const sx = Math.round(agent.gx), sy = Math.round(agent.gy);
  for (let i = 0; i < 18; i++) {
    const nx = sx + Math.round((Math.random() * 2 - 1) * radius);
    const ny = sy + Math.round((Math.random() * 2 - 1) * radius);
    if (!nav.walkable.has(k(nx, ny))) continue;
    const path = findPath(nav, sx, sy, nx, ny);
    if (path && path.length) {
      agent.path = path; agent.step = 0; agent.state = "flane";
      return true;
    }
  }
  agent.timer = 0.8 + Math.random();
  return false;
}

function goTo(agent, nav, tx, ty) {
  const path = findPath(nav, Math.round(agent.gx), Math.round(agent.gy), tx, ty);
  if (!path) return false;
  agent.path = path; agent.step = 0;
  return true;
}

// Avance le long du chemin. Renvoie true une fois arrivé.
function advance(agent, speed) {
  if (!agent.path || agent.step >= agent.path.length) { agent.moving = false; return true; }
  const wp = agent.path[agent.step];
  const dx = wp.x - agent.gx, dy = wp.y - agent.gy;
  const d = Math.hypot(dx, dy);
  if (d < 0.06) {
    agent.gx = wp.x; agent.gy = wp.y;
    agent.step++;
    return agent.step >= agent.path.length;
  }
  agent.gx += (dx / d) * speed;
  agent.gy += (dy / d) * speed;
  agent.moving = true;
  // en isométrique, on regarde vers la droite si (dx − dy) est positif
  if (Math.abs(dx - dy) > 0.01) agent.facing = (dx - dy) >= 0 ? 1 : -1;
  return false;
}

function release(a) {
  a.partner = null;
  a.state = "pause";
  a.timer = 0.4 + Math.random() * 1.6;
  a.cooldown = COOLDOWN + Math.random() * 10;
  a.bubble = null;
  a.speaking = false;
  a.moving = false;
  a.path = null;
}

const TOPICS = ["dots", "note", "excl", "heart", "coin", "question"];

// ─────────────────────────────────────────────────────────────
export function updateAgents(agents, dt, nav) {
  const list = [...agents.entries()];
  const npcs = list.filter(([, a]) => a.kind === "pnj");
  const animals = list.filter(([, a]) => a.kind === "animal");

  for (const [uid, a] of list) {
    a.timer -= dt;
    if (a.cooldown > 0) a.cooldown -= dt;
    a.bubbleT += dt;

    // Si le sol sous ses pieds a été bâti entre-temps, on le replace.
    if (!nav.walkable.has(k(Math.round(a.gx), Math.round(a.gy))) && a.state !== "parle") {
      const near = nav.nearestWalkable(a.gx, a.gy);
      if (near) { a.gx = near.x; a.gy = near.y; a.path = null; a.state = "pause"; a.timer = 0.5; }
    }

    switch (a.state) {
      case "parle": {
        a.moving = false;
        const other = a.partner ? agents.get(a.partner) : null;
        if (!other || other.partner !== uid) { release(a); break; }
        a.facing = other.gx + other.gy > a.gx + a.gy ? 1 : -1;
        // un seul des deux pilote l'alternance, sinon les deux se taisent
        if (uid < a.partner && a.bubbleT > 2.2) {
          a.bubbleT = 0; other.bubbleT = 0;
          const next = !a.speaking;
          a.speaking = next; other.speaking = !next;
          const who = next ? a : other;
          who.bubble = TOPICS[(Math.random() * TOPICS.length) | 0];
          (next ? other : a).bubble = null;
        }
        if (a.timer <= 0) { release(a); release(other); }
        break;
      }

      case "caresse": {
        a.moving = false;
        const beast = a.partner ? agents.get(a.partner) : null;
        if (!beast) { release(a); break; }
        a.facing = beast.gx + beast.gy > a.gx + a.gy ? 1 : -1;
        a.bubble = "heart"; a.speaking = true;
        beast.bubble = "heart"; beast.speaking = true;
        beast.moving = false;
        if (a.timer <= 0) {
          beast.pettedBy = null;
          beast.bubble = null; beast.speaking = false;
          beast.cooldown = COOLDOWN;
          beast.state = "pause"; beast.timer = 1;
          release(a);
        }
        break;
      }

      case "rejoint": {
        const other = a.partner ? agents.get(a.partner) : null;
        if (!other) { release(a); break; }
        if (a.timer <= 0) { release(a); if (other.partner === uid) release(other); break; }
        if (dist(a, other) <= (other.kind === "animal" ? PET_DISTANCE : TALK_RADIUS)) {
          if (other.kind === "animal") {
            a.state = "caresse"; a.timer = PET_TIME; a.path = null; a.moving = false;
          } else if (other.state === "rejoint" || other.state === "parle") {
            const duration = TALK_MIN + Math.random() * (TALK_MAX - TALK_MIN);
            for (const p of [a, other]) {
              p.state = "parle"; p.timer = duration; p.bubbleT = 0;
              p.moving = false; p.path = null;
            }
            a.speaking = true;
            a.bubble = TOPICS[(Math.random() * TOPICS.length) | 0];
            other.speaking = false; other.bubble = null;
          }
          break;
        }
        if (advance(a, a.kind === "animal" ? ANIMAL_SPEED : SPEED)) {
          // arrivé au bout du chemin sans avoir rejoint : on recale
          if (!goTo(a, nav, Math.round(other.gx), Math.round(other.gy))) { release(a); }
        }
        break;
      }

      case "pause":
        a.moving = false;
        a.bubble = null;
        if (a.timer <= 0) wanderTo(a, nav, a.kind === "animal" ? 4 : 7);
        break;

      default: // flâne
        if (advance(a, a.kind === "animal" ? ANIMAL_SPEED : SPEED)) {
          a.state = "pause";
          a.timer = (a.kind === "animal" ? 2 : 0.8) + Math.random() * 3;
        }
        break;
    }
  }

  // ── Nouvelles conversations : une seule à la fois sur l'archipel ──
  const talking = npcs.some(([, a]) => a.partner && a.state !== "caresse");
  if (!talking) {
    outer:
    for (let i = 0; i < npcs.length; i++) {
      const [uidA, a] = npcs[i];
      if (a.partner || a.cooldown > 0 || a.state === "parle") continue;
      for (let j = i + 1; j < npcs.length; j++) {
        const [uidB, b] = npcs[j];
        if (b.partner || b.cooldown > 0 || b.state === "parle") continue;
        if (dist(a, b) > MEET_RADIUS) continue;
        if (Math.random() > 0.01) continue;
        // Les deux convergent vers un point médian praticable.
        const mx = Math.round((a.gx + b.gx) / 2), my = Math.round((a.gy + b.gy) / 2);
        const spot = nav.walkable.has(k(mx, my)) ? { x: mx, y: my } : { x: Math.round(b.gx), y: Math.round(b.gy) };
        if (!goTo(a, nav, spot.x, spot.y)) continue;
        goTo(b, nav, spot.x, spot.y);
        a.partner = uidB; b.partner = uidA;
        a.state = "rejoint"; b.state = "rejoint";
        a.timer = 25; b.timer = 25;
        break outer;
      }
    }
  }

  // ── Caresses : un animal n'est caressé que par UN habitant ────
  for (const [uidA, animal] of animals) {
    if (animal.pettedBy || animal.cooldown > 0) continue;
    for (const [uidN, npc] of npcs) {
      if (npc.partner || npc.cooldown > 0 || npc.state === "parle") continue;
      if (dist(npc, animal) > PET_RADIUS) continue;
      if (Math.random() > 0.012) continue;
      if (!goTo(npc, nav, Math.round(animal.gx), Math.round(animal.gy))) continue;
      npc.partner = uidA;
      npc.state = "rejoint";
      npc.timer = 22;
      animal.pettedBy = uidN;
      animal.path = null;
      animal.state = "pause";
      animal.timer = 30;
      break;
    }
  }
}

// ── Bulle de conversation ─────────────────────────────────────
export function drawBubble(ctx, x, y, s, topic, t) {
  const w = 34 * s, h = 24 * s, r = 8 * s;
  const bob = Math.sin(t * 3) * 1.5 * s;
  const bx = x, by = y - 78 * s + bob;

  ctx.save();
  ctx.lineWidth = Math.max(1.2, 2.6 * s);
  ctx.strokeStyle = PAL.inkDeep;
  ctx.fillStyle = "#fdf6e8";
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(bx - w / 2, by - h / 2, w, h, r);
  else ctx.rect(bx - w / 2, by - h / 2, w, h);
  ctx.fill(); ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(bx - 5 * s, by + h / 2 - 1);
  ctx.lineTo(bx + 1 * s, by + h / 2 + 8 * s);
  ctx.lineTo(bx + 6 * s, by + h / 2 - 1);
  ctx.closePath();
  ctx.fillStyle = "#fdf6e8"; ctx.fill();
  ctx.strokeStyle = PAL.inkDeep;
  ctx.beginPath();
  ctx.moveTo(bx - 5 * s, by + h / 2 - 1);
  ctx.lineTo(bx + 1 * s, by + h / 2 + 8 * s);
  ctx.lineTo(bx + 6 * s, by + h / 2 - 1);
  ctx.stroke();

  const cx = bx, cy = by;
  ctx.fillStyle = PAL.inkDeep;
  if (topic === "dots") {
    for (let i = 0; i < 3; i++) {
      ctx.globalAlpha = 0.35 + 0.65 * Math.max(0, Math.sin(t * 4 - i * 0.7));
      ctx.beginPath(); ctx.arc(cx - 8 * s + i * 8 * s, cy, 2.2 * s, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
  } else if (topic === "note") {
    ctx.fillStyle = PAL.terraDark;
    ctx.beginPath(); ctx.ellipse(cx - 3 * s, cy + 4 * s, 3.4 * s, 2.6 * s, -0.3, 0, 7); ctx.fill();
    ctx.fillRect(cx - 0.5 * s, cy - 7 * s, 1.8 * s, 11 * s);
    ctx.fillRect(cx - 0.5 * s, cy - 7 * s, 6 * s, 2 * s);
  } else if (topic === "excl") {
    ctx.fillStyle = PAL.terra;
    ctx.fillRect(cx - 1.4 * s, cy - 7 * s, 3 * s, 8 * s);
    ctx.beginPath(); ctx.arc(cx, cy + 5 * s, 1.9 * s, 0, Math.PI * 2); ctx.fill();
  } else if (topic === "heart") {
    ctx.fillStyle = "#d9564a";
    ctx.beginPath();
    ctx.moveTo(cx, cy + 6 * s);
    ctx.bezierCurveTo(cx - 12 * s, cy - 3 * s, cx - 4 * s, cy - 10 * s, cx, cy - 3 * s);
    ctx.bezierCurveTo(cx + 4 * s, cy - 10 * s, cx + 12 * s, cy - 3 * s, cx, cy + 6 * s);
    ctx.fill();
  } else if (topic === "coin") {
    ctx.fillStyle = PAL.lampWarm;
    ctx.beginPath(); ctx.arc(cx, cy, 6 * s, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = "#b98a22"; ctx.lineWidth = 1.6 * s; ctx.stroke();
  } else {
    ctx.fillStyle = PAL.clayDark;
    ctx.font = `700 ${Math.round(15 * s)}px system-ui, sans-serif`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText("?", cx, cy + 1);
  }
  ctx.restore();
}
